import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

export function getMonth(month = dayjs().month()) {
  month = Math.floor(month);
  const year = dayjs().year();
  const firstDayOfTheMonth = dayjs(new Date(year, month, 1)).day();
  let currentMonthCount = 0 - firstDayOfTheMonth;
  const daysMatrix = new Array(5).fill([]).map(() => {
    return new Array(7).fill(null).map(() => {
      currentMonthCount++;
      return dayjs(new Date(year, month, currentMonthCount));
    });
  });
  return daysMatrix;
}

/**
 * Format date to dd-mm-yyyy format
 * @param {string|Date|dayjs.Dayjs} date - Date string, Date object, or dayjs object
 * @returns {string} Formatted date string in dd-mm-yyyy format, or empty string if invalid
 */
export function formatDateToDDMMYYYY(date) {
  if (!date) return '';

  try {
    if (dayjs.isDayjs(date)) {
      return date.isValid() ? date.format('DD-MM-YYYY') : '';
    }

    if (date instanceof Date) {
      const d = dayjs(date);
      return d.isValid() ? d.format('DD-MM-YYYY') : '';
    }

    const s = String(date).trim();
    if (!s) return '';

    // ISO date or datetime (API / forms: YYYY-MM-DD)
    let parsed = dayjs(s, 'YYYY-MM-DD', true);
    if (parsed.isValid()) return parsed.format('DD-MM-YYYY');

    parsed = dayjs(s, 'YYYY-MM-DD HH:mm:ss', true);
    if (parsed.isValid()) return parsed.format('DD-MM-YYYY');

    // Payments API: DATE_FORMAT(..., '%d/%m/%Y') — must parse as day-first, not US mm/dd
    parsed = dayjs(s, 'DD/MM/YYYY', true);
    if (parsed.isValid()) return parsed.format('DD-MM-YYYY');

    parsed = dayjs(s, 'DD-MM-YYYY', true);
    if (parsed.isValid()) return parsed.format('DD-MM-YYYY');

    // Last resort (timestamps, etc.)
    parsed = dayjs(s);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : '';
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Check if user has ViewOnly or FullAccess permission
 * @param {Array<string>} permissions - Array of permission strings
 * @param {string} permissionName - Name of the permission (e.g., "mumeneen", "receipts")
 * @returns {boolean} True if user has ViewOnly or FullAccess permission
 */
export const hasPermission = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.includes(`${permissionName}.ViewOnly`) || 
         permissions.includes(`${permissionName}.FullAccess`);
};

/**
 * Check if user has FullAccess permission
 * @param {Array<string>} permissions - Array of permission strings
 * @param {string} permissionName - Name of the permission (e.g., "mumeneen", "receipts")
 * @returns {boolean} True if user has FullAccess permission
 */
export const hasFullAccess = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.includes(`${permissionName}.FullAccess`);
};

/**
 * Check if user has ViewOnly permission (but not FullAccess)
 * @param {Array<string>} permissions - Array of permission strings
 * @param {string} permissionName - Name of the permission (e.g., "mumeneen", "receipts")
 * @returns {boolean} True if user has ViewOnly permission but not FullAccess
 */
export const hasViewOnly = (permissions, permissionName) => {
  if (!permissions || !Array.isArray(permissions)) return false;
  return permissions.includes(`${permissionName}.ViewOnly`) && 
         !permissions.includes(`${permissionName}.FullAccess`);
};

/** User IDs/usernames who always see Niyaz Calendar regardless of role */
export const NIYAZ_CALENDAR_ALLOWED_IDS = [40413682, 40413333, 473, 180];

export const canShowNiyazCalendarRegardlessOfAccess = (user) => {
  if (!user) return false;
  const id = user.id != null ? String(user.id).trim() : "";
  const username = user.username != null ? String(user.username).trim() : "";
  const allowedSet = new Set(NIYAZ_CALENDAR_ALLOWED_IDS.map((n) => String(n)));
  return allowedSet.has(id) || allowedSet.has(username) || (Number(id) && NIYAZ_CALENDAR_ALLOWED_IDS.includes(Number(id))) || (Number(username) && NIYAZ_CALENDAR_ALLOWED_IDS.includes(Number(username)));
};

/** User IDs who can see LQ Collection page (advance receipts) */
export const LQ_COLLECTION_ALLOWED_IDS = [180, 473];

export const canShowLqCollection = (user) => {
  if (!user) return false;
  const id = user.id != null ? String(user.id).trim() : "";
  const username = user.username != null ? String(user.username).trim() : "";
  const allowedSet = new Set(LQ_COLLECTION_ALLOWED_IDS.map((n) => String(n)));
  return allowedSet.has(id) || allowedSet.has(username) || (Number(id) && LQ_COLLECTION_ALLOWED_IDS.includes(Number(id))) || (Number(username) && LQ_COLLECTION_ALLOWED_IDS.includes(Number(username)));
};

/** Same allowlist as LQ Collection for Due List + Razorpay WA send */
export const canShowDueList = canShowLqCollection;
